import { Component, OnInit } from '@angular/core';
import { RouterModule } from '@angular/router';
import { Subscription } from 'rxjs';
import { ApiResponseModel } from 'src/app/core/services/api/api-response.model';
import { DataService } from 'src/app/core/services/api/data.service';
import { FilterService } from 'src/app/core/services/filter.service';
import { HeaderService } from 'src/app/core/services/header.service';
import { UiUtilityService } from 'src/app/core/services/ui-utility.service';
import { EndPointsEnum } from 'src/app/shared/enums/end-points';
import { FacilityModel } from 'src/app/shared/models/login-response.model';
import { BaseWearerModel } from 'src/app/shared/models/wearer.model';
import { environment } from 'src/environments/environment';
import { IonNav, IonicModule } from '@ionic/angular';
import { CommonModule } from '@angular/common';
import { ScrollingModule } from '@angular/cdk/scrolling';
import { SubHeaderComponent } from 'src/app/shared/components/sub-header/sub-header.component';
import { cloneDeep } from 'lodash-es';
import { wearerFilterData } from 'src/app/shared/data/filter.data';
import { FACILITY_ID_URL } from 'src/app/shared/config/app.constant';
import { WearerDetailsPage } from '../wearer-details/wearer-details.page';
import { AccessControlDirective } from 'src/app/shared/directives/access-control.directive';
import { AuthService } from 'src/app/core/services/auth.service';
import { FormsModule } from '@angular/forms';

@Component({
  standalone: true,
  selector: 'app-wearer-list',
  templateUrl: './wearer-list.page.html',
  styleUrls: ['./wearer-list.page.scss'],
  imports: [
    IonicModule,
    CommonModule,
    ScrollingModule,
    RouterModule,
    SubHeaderComponent,
    AccessControlDirective,
    FormsModule,
  ],
})
export class WearerListPage implements OnInit {
  wearerListCopy: BaseWearerModel[] = [];
  wearerList: BaseWearerModel[] = [];
  searchWearerList: BaseWearerModel[] = [];
  subscriptions: Subscription[] = [];
  selectedFacility = '';
  groups: any[] = [];
  filters: any = {};
  searchQuery = '';
  isBedSlotEnabled = false;
  isEtectRxEnabled = this.headerService.isEtectRxEnabled;

  constructor(
    private dataService: DataService<BaseWearerModel[]>,
    private filterService: FilterService,
    private headerService: HeaderService,
    private uiUtilityService: UiUtilityService,
    private nav: IonNav,
    private authService: AuthService
  ) { }

  setGroups() {
    const group = cloneDeep(wearerFilterData);
    if (!this.isEtectRxEnabled) {
      this.groups = group.filter((item: any) => item.enable !== 'isEtectRxEnabled');
    } else {
      this.groups = group;
    }
  }

  ngOnInit(): void {
    this.setGroups();
  }

  ionViewWillEnter() {
    this.subscriptions.push(
      this.headerService
        .getFacility()
        .subscribe((facility: FacilityModel) => this.onFacilityChange(facility))
    );
    this.isBedSlotEnabled = this.headerService.isBedSlotEnabled;
    this.getWearerList();
    this.sortWearerList();
  }

  sortWearerList() {
    const result = this.filterService.getFilterData(this.groups);
    this.filters = this.filterService.setSortFilter(result);
  }

  didSortWearerList() {
    const result = this.filterService.filteredData(
      this.wearerListCopy,
      this.filters
    );
    this.wearerList = result;
    this.searchWearerList = result;
  }

  async getWearerList() {
    await this.uiUtilityService.showLoader();
    const url = `${environment.apiUrl.residentManagement}${FACILITY_ID_URL}/${EndPointsEnum.getList}`;
    this.dataService
      .getData(url)
      .subscribe((response: ApiResponseModel<any>) => {
        if (response && response.success && response.data) {
          const loginData: any = this.authService.getLoginData;
          const facilityId = this.headerService.getFacilityId();
          const groups =
            loginData.facility.find(
              (item: any) => item.facilityId === facilityId
            )?.wearerGroupDefinition || [];
          const residentGroupId =
            groups.find(
              (item: any) =>
                item.wearerGroupName.toLowerCase() === 'resident' ||
                item.wearerGroupName.toLowerCase() === 'residents'
            )?.id || '';
          this.wearerListCopy = response.data?.map((item: any) => {
            item.displayName = this.uiUtilityService.getEntityDisplayName(item);
            item.sortDisplayName =
              this.uiUtilityService.getEntitySortDisplayName(item);
            item.isResident = false;
            if (residentGroupId === item.wearerGroupId) {
              item.isResident = true;
            }
            if (item.roomName) {
              item.appRoomName = `${item.roomName}${item.slotName && this.isBedSlotEnabled
                ? `-${item.slotName}`
                : ''
                }`;
            }
            item.incontinence = 'OTHER';
            if (item.etectId === null || item.etectId === undefined) {
              item.incontinence = 'OTHER';
             } else if (
               item.etectMonitoringStatus === 'disabled' ||
               item.etectAlertStatus === 'paused'
             ) {
               item.incontinence = 'INACTIVE';
             } else {
               item.incontinence = 'ACTIVE';
             }
            return item;
          });
          this.didSortWearerList();
        } else {
          const header = 'Error';
          this.uiUtilityService.showAlert(response.errorMessage!, header);
        }
      });
  }

  groupByAlphabet(
    wearer: BaseWearerModel,
    wearerIndex: number,
    wearerList: BaseWearerModel[]
  ): string {
    if (wearerIndex === 0) {
      return (wearer.firstName || '')[0]?.toUpperCase();
    }
    const prevWearerLetter = (wearerList[wearerIndex - 1]?.firstName ||
      '')[0]?.toUpperCase();
    const currentWearerLetter = (wearer.firstName || '')[0]?.toUpperCase();
    if (prevWearerLetter !== currentWearerLetter) {
      return currentWearerLetter;
    }
    return '';
  }

  navigateToDetailsPage(wearer: BaseWearerModel) {
    if (this.authService.isFeatureEnabled('getWearerDetail')) {
      this.nav.push(WearerDetailsPage, { id: wearer.id, mode: 'detail' });
    }
  }

  navigateToAddWearer() {
    this.nav.push(WearerDetailsPage);
  }

  onFacilityChange(facility: FacilityModel) {
    if (facility.facilityId !== this.selectedFacility) {
      this.getWearerList();
    }
  }

  onFilterApply(data: any) {
    this.searchQuery = '';
    this.filters = this.filterService.setSortFilter(data);
    this.didSortWearerList();
  }

  onFilterCancel(data: any) {
    this.groups = data;
  }

  onFilterReset() {
    this.searchQuery = '';
    this.setGroups();
    this.sortWearerList();
    this.didSortWearerList();
  }

  dismiss() {
    if (this.uiUtilityService.pageModal) {
      this.uiUtilityService.pageModal.dismiss();
    }
  }

  ionViewWillLeave(): void {
    this.subscriptions.forEach((subscription) => {
      if (subscription) {
        subscription.unsubscribe();
      }
    });
  }

  handleChange(event: any) {
    this.wearerList = this.searchWearerList;
    this.searchQuery = event.target.value;
    if (this.searchQuery && this.searchQuery.trim() !== '') {
      this.wearerList = this.wearerList.filter((term: any) => {
        const query = this.searchQuery.trim().toLowerCase();
        return (
          term.displayName?.toLowerCase().indexOf(query) !== -1 ||
          term.bedName?.toLowerCase().indexOf(query) > -1 ||
          term.appRoomName?.toLowerCase().indexOf(query) > -1 ||
	  (term.etectId !== null && term.etectId !== undefined &&
          String(term.etectId).toLowerCase().indexOf(query) > -1)
        );
      });
    } else if (this.searchQuery === '') {
      this.wearerList = this.searchWearerList;
    }
  }
}
